import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TSIconModule } from 'tsui';

import { Account } from 'app/core/models/account.types';
import { CelesteSdkService } from '@core/services/celeste/celeste-sdk.service';

const RETRY_TOOLTIP = 'Celeste failed to load. Click to retry.';

type CelesteToggleMode = 'hidden' | 'sdk' | 'link';

/**
 * Header entry point for Celeste, driven by the account passed in via `[account]`:
 *
 * - `tahoe_tenant_id` and `tahoe_tenant_alias` present → toggles the embedded Celeste Lite chat (SDK).
 * - Otherwise, if `celeste_url` is present              → links out to that URL in a new tab.
 * - Neither → hidden.
 *
 * Styled to match the header icon box it replaced (the former `celeste_url` link):
 * a 40x40 box with the icon filling it, no mat-icon-button ripple.
 *
 * In SDK mode the box also reflects the script load state: a spinner overlay while the
 * SDK script loads, and a rotating refresh icon (with tooltip) that re-attempts the load
 * when the script fails to load.
 */
@Component({
  selector: 'celeste-toggle',
  standalone: true,
  imports: [NgTemplateOutlet, MatProgressSpinnerModule, MatTooltipModule, TSIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (mode() === 'sdk') {
      <div class="celeste-toggle-host mr-2 ml-2">
        <button
          type="button"
          class="celeste-toggle flex items-center justify-center h-10 w-10"
          title="Open Celeste"
          [disabled]="loading() || failed()"
          (click)="openChat()"
        >
          <ng-container [ngTemplateOutlet]="icon"></ng-container>
        </button>

        @if (loading()) {
          <span class="celeste-toggle-host__overlay" aria-busy="true" aria-live="polite">
            <mat-progress-spinner mode="indeterminate" [diameter]="20" />
          </span>
        } @else if (failed()) {
          <button
            type="button"
            class="celeste-toggle-host__overlay celeste-toggle-host__retry"
            [matTooltip]="retryTooltip"
            (click)="retry()"
          >
            <ts-icon class="celeste-toggle-host__retry-icon" icon="ts_refresh" size="small" color="white" />
          </button>
        }
      </div>
    } @else if (mode() === 'link') {
      <a
        class="celeste-toggle mr-2 ml-2 flex items-center justify-center h-10 w-10"
        [href]="celesteUrl()"
        target="_blank"
        rel="noopener noreferrer"
        title="Open Celeste"
      >
        <ng-container [ngTemplateOutlet]="icon"></ng-container>
      </a>
    }

    <ng-template #icon>
      <img
        class="select-none pointer-events-none w-full h-full object-contain"
        src="assets/icons/celeste.svg"
        alt="Open Celeste"
      />
    </ng-template>
  `,
  styles: [
    `
      .celeste-toggle {
        appearance: none;
        background: transparent;
        border: 0;
        padding: 0.25rem 0 0;
        cursor: pointer;
      }

      .celeste-toggle:disabled {
        cursor: default;
      }

      .celeste-toggle-host {
        position: relative;
        display: inline-flex;
      }

      .celeste-toggle-host__overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 9999px;
        background-color: rgba(17, 24, 39, 0.6);
      }

      .celeste-toggle-host__retry {
        appearance: none;
        border: 0;
        cursor: pointer;
      }

      .celeste-toggle-host__retry-icon {
        display: inline-flex;
        transition: transform 0.15s ease-in-out;
      }

      .celeste-toggle-host__retry:hover .celeste-toggle-host__retry-icon {
        transform: rotate(-15deg);
      }
    `,
  ],
})
export class CelesteToggleComponent {
  private readonly celesteSdkService = inject(CelesteSdkService);

  readonly account = input<Account | null | undefined>();

  protected readonly mode = computed<CelesteToggleMode>(() => this.resolveMode(this.account()));
  protected readonly celesteUrl = computed<string>(() => this.account()?.celeste_url ?? '');

  protected readonly loading = this.celesteSdkService.loading;
  protected readonly failed = this.celesteSdkService.failed;
  protected readonly retryTooltip = RETRY_TOOLTIP;

  protected openChat(): void {
    this.celesteSdkService.toggle();
  }

  protected retry(): void {
    this.celesteSdkService.loadSdk(this.account());
  }

  private resolveMode(account: Account | null | undefined): CelesteToggleMode {
    const hasRequiredTahoeFields =
      this.isPresent(account?.tahoe_tenant_id) && this.isPresent(account?.tahoe_tenant_alias);

    if (hasRequiredTahoeFields) {
      return 'sdk';
    }

    if (this.isPresent(account?.celeste_url)) {
      return 'link';
    }

    return 'hidden';
  }

  private isPresent(value: string | undefined): boolean {
    return !!value?.trim();
  }
}
